'use client'

import { Icon } from '@dc-shared-web/components/base/Icon'
import MultiSelect from '@dc-shared-web/components/base/MultiSelect'
import { CopyLink } from '@dc-shared-web/components/features/ShareDialog/CopyLink'
import { SharedUsersList } from '@dc-shared-web/components/features/ShareDialog/SharedUsersList'
import { Button } from '@dc-shared-web/components/shadcn/button'
import { useDebounce } from '@uidotdev/usehooks'

import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from '@dc-shared-web/components/shadcn/dialog'

import { Textarea } from '@dc-shared-web/components/shadcn/textarea'
import { useFSTrackEvent, useSearchUser } from '@dc-shared-web/hooks'
import { isValidEmail } from '@dc-shared-web/lib/utils'
import {
  faCircleExclamation,
  faXmark,
} from '@document-crunch/pro-regular-svg-icons'
import { useEffect, useRef, useState } from 'react'
import { useShareDocument } from '@dc-shared-web/hooks/api'
import { FS } from '@dc-shared-web/lib/trackingConstants'

export type ShareActionType = 'SHARE' | 'SHARE_REVOKE'

export type Crunch = {
  id: string
  name: string
  team: string
  teamId: string
}

type ShareDialogProps = {
  open: boolean
  setOpen: (open: boolean) => void
  document: Crunch
  children?: React.ReactNode
  onActionSuccess?: (actionType: ShareActionType, emailList: string[]) => void
  onActionError?: (actionType: ShareActionType) => void
}

export function ShareDialog({
  open,
  setOpen,
  document,
  children,
  onActionSuccess,
  onActionError,
}: ShareDialogProps) {
  const [selectedUsers, setSelectedUsers] = useState<
    { value: string; label: string }[]
  >([])
  // Emails not related to the document team
  const [filteredSelectedUserEmails, setfilteredSelectedUserEmails] = useState<
    {
      value: string
      label: string
    }[]
  >([])
  const [message, setMessage] = useState<string>('')
  const [copyLinkText, setCopyLinkText] = useState<string>('Copy link')
  const [invalidEmailError, setInvalidEmailError] = useState<string>('')
  const [searchVal, setSearchVal] = useState('')
  const texareaRef = useRef<HTMLTextAreaElement>(null)
  const debouncedSearchVal = useDebounce(searchVal, 500)
  const { trackEvent } = useFSTrackEvent()

  const { mutate: shareDocMutate, isLoading: shareIsLoading } =
    useShareDocument({
      documentId: document.id,
      onSuccess: () => {
        onActionSuccess &&
          onActionSuccess(
            'SHARE',
            filteredSelectedUserEmails.map((email) => email.value)
          )
      },
      onError: () => {
        onActionError && onActionError('SHARE')
      },
    })

  const { data: searchUserData, fetchNextPage } = useSearchUser({
    active: true,
    sortField: `name`,
    limit: 20,
    teamUUID: document.teamId,
    search: debouncedSearchVal,
  })

  useEffect(() => {
    if (!open) {
      setMessage('')
      setSelectedUsers([])
      setCopyLinkText('Copy link')
      setInvalidEmailError('')
    }
  }, [open, document.id])

  const allPagesData = searchUserData?.pages.map((page) => page.data)
  const options = allPagesData?.flat().map((user) => {
    return {
      secondLabel: user.email,
      label: user.name,
      value: user.email,
    }
  })

  const onMultiSelectChange = (v: { value: string; label: string }[]) => {
    // check all emails are valid
    const invalidEmail = v.find((email) => !isValidEmail(email.value))
    if (!invalidEmail) {
      setInvalidEmailError('')
    } else if (v.length === 1) {
      setInvalidEmailError('Please enter a valid email')
    } else {
      setInvalidEmailError('One or more emails are invalid')
    }
    // check for comma delimiter and whitespace and separate emails
    const emails =
      v.length > 0
        ? v
            .filter(
              (email) => email.value === email.label || email.label === ''
            )
            .map((email) => email.value)
            .join(',')
            .split(',')
            .map((email) => email.trim())
        : []

    // We only care about this filter when a user's email is being added, not revoked
    if (v.length > selectedUsers.length) {
      const nonTeamEmails = v.filter(handleFilterTeamUser)
      setfilteredSelectedUserEmails(nonTeamEmails)
    }

    setSelectedUsers([
      ...v.filter((email) => email.value !== email.label),
      ...emails
        .filter((email) => email.length > 0)
        .map((email) => ({
          value: email,
          label: options?.find((o) => o.value === email)?.label ?? email,
        })),
    ])
  }

  // If a user already belongs to the team the document belongs to,
  // then we shouldn't send that to the consuming app (dcrunch)
  const handleFilterTeamUser = (userEmail: {
    value: string
    label: string
  }) => {
    if (searchUserData) {
      let hasAccessToTeam = false
      searchUserData.pages[0].data.forEach((userData) => {
        if (userData.email === userEmail.value) {
          hasAccessToTeam = true
        }
      })
      return !hasAccessToTeam
    }
    return true
  }

  const onMultiSelectBlur = () => {
    const tempUsers =
      searchVal.length > 0
        ? searchVal.split(',').map((email) => {
            return { value: email, label: email }
          }) || []
        : []
    const updatedUsers = [...selectedUsers, ...tempUsers]
    onMultiSelectChange(updatedUsers)
    setSearchVal('')
    texareaRef.current?.focus()
  }

  const onShare = () => {
    trackEvent(FS.CRUNCH_SHARED)
    shareDocMutate({
      uuid: document.id,
      recipients: selectedUsers.map((v) => v.value).join(',') || '',
      message: message,
      name: document.name,
    })
  }

  return (
    <Dialog onOpenChange={() => setOpen(!open)} open={open}>
      <DialogTrigger
        data-testid={`share-modal-open-btn`}
        onClick={() => {
          setOpen(true)
        }}
        asChild
      >
        {children}
      </DialogTrigger>
      <DialogContent
        className={'tw-z-50 tw-relative tw-p-6 tw-w-full tw-max-w-lg tw-scroll'}
        style={{ scrollbarColor: 'auto' }}
        data-testid='share-modal-content'
      >
        {/* header */}
        <div className='tw-flex tw-content-between'>
          <h1 className='tw-font-medium tw-truncate tw-w-full tw-text-neutral-700 tw-text-base'>
            Share &quot;{document.name}&quot;
          </h1>
          <Button
            className='tw-p-0 tw--mt-2 tw--mr-2'
            data-testid={`share-modal-close-btn`}
            variant='ghost'
            size={'icon'}
            onClick={() => setOpen(false)}
          >
            <Icon icon={faXmark} size='xl' className='tw-text-neutral-400' />
          </Button>
        </div>
        {/* User search */}
        <MultiSelect
          value={selectedUsers}
          onValueChange={onMultiSelectChange}
          onScrollEndReached={fetchNextPage}
          allowNew
          isError={!!invalidEmailError}
          setSearchValue={setSearchVal}
          placeholder='Add by email or name'
          options={options || []}
          data-testid='share-modal-multi-select'
          onBlur={onMultiSelectBlur}
        />
        {invalidEmailError && (
          <p className='tw-flex tw-items-center tw-text-sm tw-text-red-600 tw--mt-3 tw-pl-1'>
            <Icon
              icon={faCircleExclamation}
              size='sm'
              className='tw-text-red-600 tw-mr-1'
            />
            {invalidEmailError}
          </p>
        )}
        {/* Share message */}
        <Textarea
          placeholder='Optional message to be included in the email notification'
          className='tw-rounded-lg tw-border tw-border-neutral-200 tw-px-2 tw-w-full placeholder:tw-text-neutral-400 focus:tw-outline-2 focus:tw--outline-offset-2 focus:tw-outline-blue-600 tw-text-neutral-700'
          value={message}
          onInput={(e) => setMessage(e.currentTarget.value)}
          ref={texareaRef}
        />
        {/* Shared user list */}
        <SharedUsersList
          document={document}
          onActionSuccess={onActionSuccess}
          onActionError={onActionError}
        />
        {/* Action buttons */}
        <div className='tw-flex tw-justify-end tw-gap-4'>
          <Button
            variant='outline'
            size='sm'
            onClick={() => setOpen(false)}
            data-testid={`share-modal-cancel-btn`}
            className='tw-text-neutral-700'
          >
            Cancel
          </Button>
          <Button
            size='sm'
            onClick={onShare}
            data-testid={`share-modal-save-btn`}
            className='tw-font-medium'
            disabled={
              !!invalidEmailError ||
              selectedUsers.length === 0 ||
              shareIsLoading
            }
          >
            {shareIsLoading ? 'Sharing...' : 'Share'}
          </Button>
        </div>
        {/* Copy link */}
        <CopyLink
          documentId={document.id}
          copyLinkText={copyLinkText}
          setCopyLinkText={setCopyLinkText}
        />
      </DialogContent>
    </Dialog>
  )
}
